import { Page } from '../types';
import { blobPath, cdnUrl } from '../constants';

export const guideGalleryBaseUrl =
  `${cdnUrl}${blobPath}/s3/prod.beprotravel/132/BeTop/CMSGALLERY/guides`.replace(/\/+$/, '');

export interface GuideProfile {
  fullName: string;
  photo: string;
  additionalPhotos: string[];
  bio: string;
  experience: string;
  education: string;
  specialization: string;
  destinations: string;
  languages: string;
  tours: string;
  reviews: string;
}

const formatGuideText = (value: string) =>
  value
    .replace(/\s*;\s*/g, ', ')
    .replace(/\s{2,}/g, ' ')
    .trim();

const pick = (data: Record<string, unknown>, keys: string[], format = true) => {
  for (const key of keys) {
    const value = data[key];
    if (typeof value === 'string' && value.trim()) {
      return format ? formatGuideText(value) : value.trim();
    }
  }
  return '';
};

const pickPhotoList = (data: Record<string, unknown>, keys: string[]) => {
  const raw = pick(data, keys, false);
  if (!raw) return [];
  return raw
    .split(/[;,]/)
    .map((item) => item.trim())
    .filter(Boolean);
};

const decodeHtmlEntities = (value: string) =>
  value
    .replace(/&nbsp;/gi, ' ')
    .replace(/&quot;/g, '"')
    .replace(/&#39;|&apos;/g, "'")
    .replace(/&laquo;/gi, '«')
    .replace(/&raquo;/gi, '»')
    .replace(/&mdash;/gi, '—')
    .replace(/&ndash;/gi, '–')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\u00a0/g, ' ');

const normalizeGuideBody = (raw: string) => {
  const withoutTags = decodeHtmlEntities(
    raw
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>/gi, '\n')
      .replace(/<[^>]+>/g, ' '),
  )
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{2,}/g, '\n')
    .trim();

  const start = withoutTags.indexOf('{');
  const end = withoutTags.lastIndexOf('}');
  if (start === -1 || end === -1 || end <= start) return withoutTags;
  return withoutTags.slice(start, end + 1);
};

const looksLikeGuideJson = (value: string) => {
  const normalized = normalizeGuideBody(value);
  return (
    normalized.startsWith('{') &&
    /"(Full Name|Source Information|Experience|Countries \/ Destinations|Полное имя|Опыт)"/i.test(
      normalized,
    )
  );
};

export const parseGuideProfile = (raw?: string): GuideProfile | null => {
  if (!raw || typeof raw !== 'string') return null;
  const trimmed = normalizeGuideBody(raw);
  if (!trimmed.startsWith('{')) return null;

  try {
    const parsed = JSON.parse(trimmed);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    const data = parsed as Record<string, unknown>;

    return {
      fullName: pick(data, ['Полное имя', 'full_name', 'fullName', 'Full Name', 'name', 'Name']),
      photo: pick(data, ['Фото', 'photo', 'Photo'], false),
      additionalPhotos: pickPhotoList(data, [
        'Дополнительные фото',
        'Additional Photos',
        'additional_photos',
        'additionalPhotos',
      ]),
      bio: pick(data, [
        'Исходная информация',
        'source_info',
        'sourceInfo',
        'Source Information',
        'bio',
        'Bio',
        'about',
      ]),
      experience: pick(data, ['Опыт', 'experience', 'Experience']),
      education: pick(data, ['Образование', 'education', 'Education']),
      specialization: pick(data, ['Специализация', 'specialization', 'Specialization']),
      destinations: pick(data, [
        'Страны / направления',
        'Страны/направления',
        'Countries / Destinations',
        'countries',
        'Countries',
        'destinations',
        'Destinations',
      ]),
      languages: pick(data, ['Языки', 'languages', 'Languages']),
      tours: pick(data, ['Названия туров', 'tours', 'Tour Names', 'tour_names']),
      reviews: pick(data, ['Отзывы', 'reviews', 'Reviews']),
    };
  } catch {
    return null;
  }
};

export const getGuideLang = (page: Page) => {
  const languages = page.languages || [];
  return (
    languages.find((item) => String(item.language).toLowerCase() === 'ru') ||
    languages[0]
  );
};

export const getGuideBodyRaw = (page: Page) => {
  const lang = getGuideLang(page);
  return lang?.title?.body || page.title?.body || '';
};

export const getGuideProfile = (page: Page) => parseGuideProfile(getGuideBodyRaw(page));

export const getGuideName = (page: Page) => {
  const profile = getGuideProfile(page);
  if (profile?.fullName) return profile.fullName;

  const lang = getGuideLang(page);
  return (
    lang?.title?.menu ||
    lang?.title?.desc ||
    page.title?.menu ||
    page.title?.desc ||
    ''
  );
};

export const normalizeGuideName = (name?: string) =>
  (name || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

export const getGuideNameWords = (name?: string) =>
  normalizeGuideName(name)
    .split(/[\s,.;:/\\|_\-]+/)
    .map((word) => word.trim())
    .filter(Boolean);

const wordMatches = (left: string, right: string) =>
  left === right || left.includes(right) || right.includes(left);

export const isSameGuideName = (left?: string, right?: string) => {
  const leftWords = getGuideNameWords(left);
  const rightWords = getGuideNameWords(right);
  if (!leftWords.length || !rightWords.length) return false;

  const [shorter, longer] =
    leftWords.length <= rightWords.length
      ? [leftWords, rightWords]
      : [rightWords, leftWords];

  return shorter.every((word) => longer.some((other) => wordMatches(word, other)));
};

export const matchesGuideSearch = (guideName?: string, search?: string) => {
  const query = normalizeGuideName(search);
  if (!query) return true;

  const name = normalizeGuideName(guideName);
  if (!name) return false;
  if (name.includes(query)) return true;

  const queryWords = getGuideNameWords(search);
  const nameWords = getGuideNameWords(guideName);
  if (!queryWords.length || !nameWords.length) return false;

  return queryWords.every((word) => nameWords.some((other) => wordMatches(word, other)));
};

export const getGuidesListPath = (highlightName?: string) => {
  if (!highlightName?.trim()) return '/guides';
  return `/guides?guide=${encodeURIComponent(highlightName.trim())}`;
};

export const getGuideSlug = (page: Page) => {
  const lang = getGuideLang(page);
  const url = lang?.title?.url || page.title?.url || '';
  return url.replace(/^\/+/, '').replace(/^pages\//i, '');
};

export const getGuidePath = (page: Page) => {
  const slug = getGuideSlug(page);
  if (!slug) return '';
  if (/^https?:\/\//i.test(slug)) return slug;
  return `/guides/${slug}`;
};

export const getGuideDestinations = (page: Page) => {
  const profile = getGuideProfile(page);
  if (profile?.destinations) return profile.destinations;

  const lang = getGuideLang(page);
  return lang?.title?.sub || page.title?.sub || '';
};

export const getGuideBodyHtml = (page: Page) => {
  const raw = getGuideBodyRaw(page);
  if (!raw) return '';
  if (parseGuideProfile(raw) || looksLikeGuideJson(raw)) return '';
  return raw;
};

export const getGuideGallery = (page: Page) => {
  const lang = getGuideLang(page);
  const gallery = lang?.listOfGalleryId?.length ? lang.listOfGalleryId : page.listOfGalleryId;
  return gallery || [];
};

export const getGuideFileUrl = (fileName?: string) => {
  if (!fileName) return '';
  if (/^https?:\/\//i.test(fileName)) return fileName;
  if (fileName.startsWith('/')) return cdnUrl + blobPath + fileName;
  const encoded = fileName
    .split('/')
    .map((part) => encodeURIComponent(part))
    .join('/');
  return `${guideGalleryBaseUrl}/${encoded}`;
};

export const getGuideImageUrl = (code?: string) => {
  if (!code) return '';
  if (/^https?:\/\//i.test(code)) return code;
  return cdnUrl + blobPath + code;
};

export const getGuideAvatarUrl = (page: Page) => {
  const gallery = getGuideGallery(page);
  const fromGallery = getGuideImageUrl(gallery[0]?.code);
  if (fromGallery) return fromGallery;

  const profile = getGuideProfile(page);
  return getGuideFileUrl(profile?.photo);
};

export const getGuideAdditionalPhotoUrls = (page: Page) => {
  const profile = getGuideProfile(page);
  return (profile?.additionalPhotos || []).map(getGuideFileUrl).filter(Boolean);
};
